import { Settings as LuxonSettings } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { useCustomerSettingsContext } from "src/contexts";
import { Language } from "../../../datahandling/localization/application.i18n";
import i18n from "../../../datahandling/localization/i18n";
import { useGetApiByCompanyIdUserSettingsQuery } from "../../../datahandling/redux/api/rtkQueryCodeGen/knowledgerApi/knowledgerApiInjections";

/**
 * Hook that manages i18n initialization and language selection.
 * Synchronizes user settings, localStorage preferences, and Luxon locale.
 *
 * @returns {{ language: string; isLangReady: boolean }} - Current language and ready state
 */
export function useI18n() {
	const { selectedCompany } = useCustomerSettingsContext();
	const companyId = selectedCompany?.id ?? null;
	const { data: userSettings } = useGetApiByCompanyIdUserSettingsQuery(
		{ companyId: companyId as string },
		{ skip: !companyId },
	);
	const { setLanguage, language } = useCustomerSettingsContext();
	const selectedLanguage = userSettings?.language;
	const [isLangReady, setIsReady] = useState(false);

	const setLuxonLocaleFromI18n = useCallback(() => {
		const candidates: string[] = [];
		const primary = i18n.resolvedLanguage || i18n.language || Language.English;
		if (primary) {
			candidates.push(primary);
			if (primary.includes("_")) candidates.push(primary.replace("_", "-"));
		}

		const uniqueCandidates = Array.from(new Set(candidates.filter(Boolean)));
		const supported = Intl.DateTimeFormat.supportedLocalesOf(uniqueCandidates);
		const resolved = supported[0] || Language.English;
		LuxonSettings.defaultLocale = resolved;
	}, []);

	// Initial setup
	useEffect(() => {
		const initLang = Language.English;
		i18n.changeLanguage(initLang).then(() => {
			setLuxonLocaleFromI18n();
			setLanguage(initLang);
			setIsReady(true);
		});
	}, [setLuxonLocaleFromI18n, setLanguage]);

	// React to user settings changes
	useEffect(() => {
		if (selectedLanguage) {
			i18n.changeLanguage(selectedLanguage).then(() => {
				if (selectedLanguage === Language.English || selectedLanguage === Language.Norwegian) {
					setLanguage(selectedLanguage);
				}
				setLuxonLocaleFromI18n();
				setLanguage(selectedLanguage);
			});
		} else if (isLangReady) {
			const fallback = Language.English;
			i18n.changeLanguage(fallback).then(() => {
				setLuxonLocaleFromI18n();
				setLanguage(fallback);
			});
		}
	}, [selectedLanguage, isLangReady, setLuxonLocaleFromI18n, setLanguage]);

	// React to any other language changes triggered elsewhere
	useEffect(() => {
		const handler = (lng: string) => {
			setLuxonLocaleFromI18n();
			setLanguage(lng);
		};
		i18n.on("languageChanged", handler);
		return () => {
			i18n.off("languageChanged", handler as any);
		};
	}, [setLuxonLocaleFromI18n, setLanguage]);

	return { language, isLangReady };
}
